/**
 * Reorder elements so parent/container appears before children (containerId and parentId).
 * Fixes z-order for region backgrounds and container-bound text.
 * Does not mutate input; returns a new array.
 */

export type ElementLike = {
  id: string;
  type: string;
  containerId?: string | null;
  parentId?: string | null;
  boundElements?: Array<{ id: string; type: string }> | null;
  [key: string]: unknown;
};

/**
 * Step 1: Ensure container.boundElements includes text elements that reference it via containerId.
 * Step 2: Build edges (parentId, childId) where parentId = containerId ?? parentId.
 * Step 3: Stable topological sort so every parent appears before its children; break ties by original index.
 */
export function reorderParentBeforeChild<T extends ElementLike>(
  elements: readonly T[],
): T[] {
  if (elements.length === 0) {
    return [];
  }

  const list = [...elements];
  const elementMap = new Map<string, T>();
  for (const e of list) {
    if (e?.id != null) {
      elementMap.set(e.id, e);
    }
  }

  // Step 1 – boundElements: for every text with containerId, ensure container's boundElements includes it
  // Clone containers when updating to avoid mutating input.
  for (const elem of list) {
    if (elem.type !== "text" || !elem.containerId) {
      continue;
    }
    const container = elementMap.get(elem.containerId);
    if (!container) {
      continue;
    }

    const binding = { id: elem.id, type: "text" };
    const bound = container.boundElements;
    if (bound == null) {
      const updated = { ...container, boundElements: [binding] } as T;
      const idx = list.indexOf(container);
      if (idx !== -1) {
        list[idx] = updated;
      }
      elementMap.set(container.id, updated);
    } else if (Array.isArray(bound)) {
      const has = bound.some(
        (b: { id?: string; type?: string }) =>
          b?.id === elem.id && b?.type === "text",
      );
      if (!has) {
        const updated = {
          ...container,
          boundElements: [...bound, binding],
        } as T;
        const idx = list.indexOf(container);
        if (idx !== -1) {
          list[idx] = updated;
        }
        elementMap.set(container.id, updated);
      }
    }
  }

  // Step 2 – edges: (parentId, childId) where parentId = containerId ?? parentId
  const idToIndex = new Map<string, number>();
  list.forEach((e, i) => {
    if (e?.id != null) {
      idToIndex.set(e.id, i);
    }
  });

  type Edge = [string, string];
  const edges: Edge[] = [];
  for (const elem of list) {
    const parentId = elem.containerId ?? elem.parentId ?? undefined;
    if (!parentId || !elementMap.has(parentId)) {
      continue;
    }
    if (elem.id) {
      edges.push([parentId, elem.id]);
    }
  }

  // Step 3 – stable topological sort (Kahn), break ties by original index
  const allIds = list.map((e) => e.id).filter(Boolean);
  const inDegree = new Map<string, number>();
  const successors = new Map<string, string[]>();
  for (const id of allIds) {
    inDegree.set(id, 0);
    successors.set(id, []);
  }
  for (const [p, c] of edges) {
    inDegree.set(c, (inDegree.get(c) ?? 0) + 1);
    successors.get(p)!.push(c);
  }

  const output: T[] = [];
  const outputIds = new Set<string>();
  while (output.length < list.length) {
    const candidates = allIds.filter(
      (id) => !outputIds.has(id) && (inDegree.get(id) ?? 0) === 0,
    );
    if (candidates.length === 0) {
      const remaining = allIds.filter((id) => !outputIds.has(id));
      remaining.sort(
        (a, b) => (idToIndex.get(a) ?? 0) - (idToIndex.get(b) ?? 0),
      );
      for (const id of remaining) {
        const el = elementMap.get(id);
        if (el) {
          output.push(el);
        }
      }
      break;
    }
    candidates.sort(
      (a, b) => (idToIndex.get(a) ?? 0) - (idToIndex.get(b) ?? 0),
    );
    const chosen = candidates[0];
    const el = elementMap.get(chosen);
    if (el) {
      output.push(el);
    }
    outputIds.add(chosen);
    for (const s of successors.get(chosen) ?? []) {
      inDegree.set(s, (inDegree.get(s) ?? 0) - 1);
    }
  }

  return output;
}
